import { ColorEnum } from '@/constants/color'
import Label from '@/features/ui/buttons/components/FieldButton/parts/Label'
import ValueOrPlaceholder from '@/features/ui/buttons/components/FieldButton/parts/ValueOrPlaceholder'
import { FC, ReactNode } from 'react'
import { StyleProp, TextStyle, View, ViewStyle } from 'react-native'
import { Gesture, GestureDetector } from 'react-native-gesture-handler'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

interface ButtonProps {
    label: string
    value: string
    placeholder: string
    valueNumberOfLines?: number
    hideLabelWhenValue?: boolean
    active?: boolean
    disabled?: boolean
    onPress: () => void
    alignFrontIconToLabel?: boolean
    FrontIcon?: ReactNode
    TailIcon?: ReactNode
    style?: StyleProp<ViewStyle>
    valueTextStyle?: StyleProp<TextStyle>
}

const Button: FC<ButtonProps> = ({
    label,
    value,
    placeholder,
    valueNumberOfLines = 1,
    hideLabelWhenValue = false,
    active = false,
    disabled = false,
    onPress,
    alignFrontIconToLabel = false,
    FrontIcon,
    TailIcon,
    style,
    valueTextStyle,
}) => {
    const { styles } = useStyles(stylesheet, { active, disabled })

    const tap = Gesture.Tap().enabled(!disabled).runOnJS(true).onStart(onPress)

    const renderLabel = () => {
        if (hideLabelWhenValue && value.length > 0) return

        if (label.length > 0) {
            return (
                <View style={styles.labelContainer}>
                    {alignFrontIconToLabel && FrontIcon}
                    <Label>{label}</Label>
                </View>
            )
        }

        if (alignFrontIconToLabel) return FrontIcon
    }

    return (
        <GestureDetector gesture={tap}>
            <View collapsable={false} style={[styles.outerContainer, style]}>
                {!alignFrontIconToLabel && FrontIcon}
                <View style={{ flex: 1 }}>
                    {renderLabel()}
                    <ValueOrPlaceholder
                        value={value}
                        placeholder={placeholder}
                        numberOfLines={valueNumberOfLines}
                        textStyle={valueTextStyle}
                    />
                </View>
                {TailIcon}
            </View>
        </GestureDetector>
    )
}

const stylesheet = createStyleSheet({
    outerContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: ColorEnum.WHITE,
        borderColor: ColorEnum.GRAY_300,
        borderRadius: 8,
        borderWidth: 1,
        height: 48,
        minWidth: 150,
        maxWidth: '100%',
        paddingHorizontal: 16,
        gap: 12,
        variants: {
            active: {
                true: {
                    borderColor: ColorEnum.BRICK_700,
                },
            },
            disabled: {
                true: {
                    backgroundColor: ColorEnum.GRAY_400,
                    borderColor: ColorEnum.GRAY_500,
                },
                false: {
                    cursor: 'pointer',
                },
            },
        },
    },
    labelContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
})

export default Button
